import type { WeatherData, InferenceOutput } from '@nfa/types';

interface WeatherPanelProps {
  weather?: WeatherData;
  inference: InferenceOutput;
}

function getWeatherIcon(conditions: InferenceOutput['conditions']) {
  if (conditions.precipitation === 'high') {
    return (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15c2.483 0 4.345-3 4.345-3s1.862 3 4.345 3c2.483 0 4.345-3 4.345-3s1.862 3 4.345 3M5 19.5c1.5 0 2.5-2 2.5-2s1 2 2.5 2 2.5-2 2.5-2 1 2 2.5 2 2.5-2 2.5-2 1 2 2.5 2M12 3v3M12 9a4 4 0 100 8" />
      </svg>
    );
  }
  if (conditions.temperature_range === 'cold') {
    return (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v18m0 0l-4-4m4 4l4-4M3 12h18M7.5 4.5L12 9l4.5-4.5M7.5 19.5L12 15l4.5 4.5" />
      </svg>
    );
  }
  if (conditions.wind_exposure === 'high') {
    return (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.59 4.59A2 2 0 1111 8H2m10.59 11.41A2 2 0 1014 16H2m15.73-8.27A2.5 2.5 0 1119.5 12H2" />
      </svg>
    );
  }
  return (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function getTemperatureColor(range: InferenceOutput['conditions']['temperature_range']) {
  switch (range) {
    case 'cold': return 'text-blue-400';
    case 'cool': return 'text-cyan-400';
    case 'mild': return 'text-green-400';
    case 'warm': return 'text-orange-400';
    default: return 'text-neutral-400';
  }
}

function getPrecipitationLabel(level: InferenceOutput['conditions']['precipitation']) {
  switch (level) {
    case 'high': return 'Wet conditions likely';
    case 'moderate': return 'Some rain expected';
    case 'low': return 'Mostly dry';
    default: return 'Unknown';
  }
}

export function WeatherPanel({ weather, inference }: WeatherPanelProps) {
  const { conditions, location, timing } = inference;

  return (
    <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-5">
      <div className="flex items-start gap-4">
        {/* Weather Icon */}
        <div className="p-3 bg-neutral-800 rounded-lg text-orange-400">
          {getWeatherIcon(conditions)}
        </div>

        <div className="flex-1">
          <h3 className="font-semibold text-white mb-1">
            {location.region || location.country.charAt(0).toUpperCase() + location.country.slice(1)} in {timing.month}
          </h3>
          <p className="text-sm text-neutral-400">
            {timing.season.charAt(0).toUpperCase() + timing.season.slice(1)} conditions
          </p>
        </div>
      </div>

      {/* Weather Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-neutral-800">
        {/* Temperature */}
        <div>
          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Temperature</p>
          <p className={`font-mono font-semibold ${getTemperatureColor(conditions.temperature_range)}`}>
            {weather ? `${weather.avg_low_c}-${weather.avg_high_c}°C` : conditions.temperature_range}
          </p>
        </div>

        {/* Precipitation */}
        <div>
          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Precipitation</p>
          <p className="font-mono text-neutral-300">
            {weather ? `${weather.avg_rainfall_mm}mm` : getPrecipitationLabel(conditions.precipitation)}
          </p>
          {weather && (
            <p className="text-xs text-neutral-500">{weather.rainy_days} rainy days</p>
          )}
        </div>

        {/* Wind */}
        <div>
          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Wind</p>
          <p className="font-mono text-neutral-300 capitalize">{conditions.wind_exposure}</p>
        </div>

        {/* Daylight */}
        <div>
          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Daylight</p>
          <p className="font-mono text-neutral-300 capitalize">{conditions.daylight_hours}</p>
        </div>
      </div>

      {/* Special Conditions */}
      {(conditions.midges || inference.special_requirements.length > 0) && (
        <div className="mt-4 pt-4 border-t border-neutral-800">
          <div className="flex flex-wrap gap-2">
            {conditions.midges && (
              <span className="px-2.5 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs rounded-full">
                Midges expected
              </span>
            )}
            {inference.special_requirements.map((req, idx) => (
              <span
                key={idx}
                className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs rounded-full"
              >
                {req}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
